import { apiRequest, apiUpload } from './api';
import { VISIBILITY_API_MAP } from '@/js/core/constants/tray-constants';

/**
 * Upload a comment media file (image, audio, video, or PDF).
 *
 * The server's CommentMediaPolicy is authoritative over the accepted type; the
 * response carries the derived `mime_type` and `media_type` so the client never
 * infers embeddability from the URL, extension, or browser-provided File.type.
 *
 * @param {File} file - The file to upload
 * @returns {Promise<{data: {attachment_id: number, url: string, mime_type: string, media_type: 'image'|'audio'|'video'|'pdf'}}>}
 */
export async function uploadMedia(file) {
  const formData = new FormData();
  formData.append('file', file);
  return apiUpload('/media', formData);
}

/**
 * Create a new tray
 * @param {object} data - The tray data
 * @param {string} data.title - Tray title (required)
 * @param {string} data.visibleTo - Visibility: 'everyone', 'members', or 'private'
 * @param {string} data.commentAccess - Comment access: 'viewers', 'specific', or 'owner'
 * @param {string[]} data.visibleUserUuids - User UUIDs with explicit tray access
 * @param {string[]} data.commentableUserUuids - User UUIDs with explicit comment access
 * @param {string[]} data.managerUserUuids - User UUIDs with manager access
 * @param {string} data.description - Tray description/content
 * @param {number|null} data.featuredMediaId - WP attachment ID
 * @param {string|null} data.featuredMediaAlt - Featured media alt text
 * @param {string|null} data.featuredMediaCaption - Featured media caption
 * @param {{x: number, y: number}|null} data.focalPoint - Focal point coordinates (0-1)
 * @returns {Promise<object>} - The created tray
 */
export async function createTray({
  title,
  visibleTo,
  commentAccess = 'viewers',
  visibleUserUuids = [],
  commentableUserUuids = [],
  managerUserUuids = [],
  description,
  featuredMediaId,
  featuredMediaAlt,
  featuredMediaCaption,
  focalPoint,
}) {
  const body = buildTrayRequestBody({
    title,
    visibleTo,
    commentAccess,
    visibleUserUuids,
    commentableUserUuids,
    description,
    featuredMediaId,
    featuredMediaAlt,
    featuredMediaCaption,
    focalPoint,
  });
  body.manager_user_uuids = managerUserUuids;

  return apiRequest('/trays', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * List trays with pagination and optional filters
 * @param {object} options - Pagination and filter options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.perPage - Items per page (default: 20)
 * @param {string[]} options.filters - Filter values: 'owned', 'shared' (default: [])
 * @returns {Promise<object>} - Paginated tray list
 */
export async function listTrays({ page = 1, perPage = 20, filters = [] } = {}) {
  const params = new URLSearchParams();
  params.set('page', page);
  params.set('per_page', perPage);
  filters.forEach(f => params.append('filter[]', f));
  return apiRequest(`/trays?${params.toString()}`);
}

/**
 * Get a single tray by UUID
 * @param {string} uuid - The tray UUID
 * @returns {Promise<object>} - The tray data
 */
export async function getTray(uuid) {
  return apiRequest(`/trays/${uuid}`);
}

/**
 * Update a tray
 * @param {string} uuid - The tray UUID
 * @param {object} data - The tray data to update
 * @returns {Promise<object>} - The updated tray
 */
export async function updateTray(uuid, data) {
  return apiRequest(`/trays/${uuid}`, {
    method: 'PUT',
    body: JSON.stringify(buildTrayRequestBody(data, { partial: true })),
  });
}

function buildTrayRequestBody(data, { partial = false } = {}) {
  const body = {};
  const commentAccess = data.commentAccess === undefined ? 'viewers' : data.commentAccess;
  const visibleUserUuids = data.visibleUserUuids === undefined ? [] : data.visibleUserUuids;
  const commentableUserUuids =
    data.commentableUserUuids === undefined ? [] : data.commentableUserUuids;

  if (!partial || data.title !== undefined) {
    body.title = data.title;
  }

  if (!partial || data.visibleTo !== undefined) {
    body.visibility = VISIBILITY_API_MAP[data.visibleTo] || 'me';
  }

  if (!partial || data.commentAccess !== undefined) {
    body.comment_access = commentAccess;
  }

  if (!partial || data.visibleUserUuids !== undefined) {
    body.visible_user_uuids = visibleUserUuids;
  }

  if (!partial || data.commentableUserUuids !== undefined) {
    body.commentable_user_uuids = commentableUserUuids;
  }

  if (!partial || data.description !== undefined) {
    body.content = partial ? data.description : data.description || '';
  }

  if (
    partial ? Object.prototype.hasOwnProperty.call(data, 'featuredMediaId') : data.featuredMediaId
  ) {
    body.featured_media_id = data.featuredMediaId;
  }

  if (
    partial
      ? Object.prototype.hasOwnProperty.call(data, 'featuredMediaAlt')
      : data.featuredMediaAlt !== undefined
  ) {
    body.featured_media_alt = data.featuredMediaAlt ?? '';
  }

  if (
    partial
      ? Object.prototype.hasOwnProperty.call(data, 'featuredMediaCaption')
      : data.featuredMediaCaption !== undefined
  ) {
    body.featured_media_caption = data.featuredMediaCaption ?? '';
  }

  if (partial ? data.focalPoint !== undefined : data.focalPoint) {
    body.focal_point_x = data.focalPoint?.x ?? null;
    body.focal_point_y = data.focalPoint?.y ?? null;
  }

  return body;
}

/**
 * Delete a tray
 * @param {string} uuid - The tray UUID
 * @returns {Promise<null>}
 */
export async function deleteTray(uuid) {
  return apiRequest(`/trays/${uuid}`, {
    method: 'DELETE',
  });
}

/**
 * Get comments for a tray with pagination
 * @param {string} uuid - The tray UUID
 * @param {object} options - Pagination options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.perPage - Items per page (default: 20)
 * @returns {Promise<object>} - Paginated comment list
 */
export async function getTrayComments(uuid, { page = 1, perPage = 20 } = {}) {
  return apiRequest(`/trays/${uuid}/comments?page=${page}&per_page=${perPage}`);
}

/**
 * Create a comment on a tray
 * @param {string} trayUuid - The tray UUID
 * @param {object} data - The comment data
 * @param {string} data.content - Comment content (HTML)
 * @param {Array<{id: number, alt: string, caption: string}>|null} [data.media] - Media items
 * @returns {Promise<object>} - The created comment
 */
export async function createComment(trayUuid, { content, media } = {}) {
  const body = { content: content || '' };

  if (media && media.length > 0) {
    body.media = media;
  }

  return apiRequest(`/trays/${trayUuid}/comments`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Update a comment
 * @param {string} trayUuid - The tray UUID
 * @param {string} commentUuid - The comment UUID
 * @param {object} data - The comment data
 * @param {string} [data.content] - Updated comment content (HTML)
 * @param {Array<{id: number, alt: string, caption: string}>|null} [data.media] - Media items (empty array to remove all)
 * @returns {Promise<object>} - The updated comment
 */
export async function updateComment(trayUuid, commentUuid, data) {
  const body = {};

  if (data.content !== undefined) body.content = data.content;
  if ('media' in data) body.media = data.media;

  return apiRequest(`/trays/${trayUuid}/comments/${commentUuid}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Delete a comment
 * @param {string} trayUuid - The tray UUID
 * @param {string} commentUuid - The comment UUID
 * @returns {Promise<null>}
 */
export async function deleteComment(trayUuid, commentUuid) {
  return apiRequest(`/trays/${trayUuid}/comments/${commentUuid}`, {
    method: 'DELETE',
  });
}

/**
 * List managers for a tray
 * @param {string} trayUuid - The tray UUID
 * @returns {Promise<object>} - Manager list
 */
export async function listManagers(trayUuid) {
  return apiRequest(`/trays/${trayUuid}/managers`);
}

/**
 * List selected members for a tray
 * @param {string} trayUuid - The tray UUID
 * @returns {Promise<object>} - Member list
 */
export async function listMembers(trayUuid) {
  return apiRequest(`/trays/${trayUuid}/members`);
}

/**
 * Add a manager to a tray
 * @param {string} trayUuid - The tray UUID
 * @param {string} userUuid - The user UUID to add as manager
 * @returns {Promise<object>} - The created manager
 */
export async function addManager(trayUuid, userUuid) {
  return apiRequest(`/trays/${trayUuid}/managers`, {
    method: 'POST',
    body: JSON.stringify({ user_uuid: userUuid }),
  });
}

/**
 * Remove a manager from a tray
 * @param {string} trayUuid - The tray UUID
 * @param {string} managerUuid - The tray_user UUID of the manager
 * @returns {Promise<null>}
 */
export async function removeManager(trayUuid, managerUuid) {
  return apiRequest(`/trays/${trayUuid}/managers/${managerUuid}`, {
    method: 'DELETE',
  });
}

/**
 * Search users for the manager picker
 * @param {string} query - Search query (min 2 chars)
 * @returns {Promise<object>} - User search results
 */
export async function searchUsers(query) {
  return apiRequest(`/trays/users/search?q=${encodeURIComponent(query)}`);
}

/**
 * Get comment status for polling
 * @param {string} trayUuid - The tray UUID
 * @param {string|null} since - Optional ISO datetime to check for new comments since
 * @returns {Promise<object>} - Status object with total, latest_updated_at, and optionally new_comment_uuids
 */
export async function getCommentStatus(trayUuid, since = null) {
  const params = since ? `?since=${encodeURIComponent(since)}` : '';
  return apiRequest(`/trays/${trayUuid}/comments/status${params}`);
}
